import { Alert, Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useTheme } from '@mui/material/styles';

import type { CustomerPerformance } from '@/types/generated';
import { formatMoneyCompact } from '@/features/reporting/formatting';

/**
 * Top customers by revenue, and margin by customer.
 *
 * HORIZONTAL BARS. Customer names are long and a vertical bar chart turns them into 45-degree
 * unreadable stubs; laid on their side they are just labels.
 *
 * THE MARGIN CHART EXCLUDES THIN SAMPLES rather than drawing them faintly. "Kilimanjaro Freight,
 * 61% margin" over one quote is not a weak signal to be read with care — at bar-chart resolution
 * it is indistinguishable from a strong one, and it will be the tallest bar on the chart. What is
 * left out is stated underneath, which is the difference between filtering and hiding.
 */
const MIN_MARGIN_SAMPLE = 5;

export function CustomerCharts({
  customers,
  currency,
}: {
  customers: CustomerPerformance[];
  currency: string;
}) {
  const theme = useTheme();

  // Ascending, because a horizontal bar chart draws the first item at the bottom — sorted
  // descending it renders upside down.
  const byRevenue = [...customers].sort((a, b) => a.won_value_minor - b.won_value_minor).slice(-10);

  const credible = customers.filter(
    (c) => c.avg_margin_basis_points !== null && c.margin_sample >= MIN_MARGIN_SAMPLE,
  );
  const byMargin = [...credible]
    .sort((a, b) => (a.avg_margin_basis_points ?? 0) - (b.avg_margin_basis_points ?? 0))
    .slice(-10);

  const excluded = customers.length - credible.length;

  return (
    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Top customers
          </Typography>
          <Box sx={{ height: 320 }}>
            {byRevenue.length === 0 ? (
              <Empty>No won business in this window.</Empty>
            ) : (
              <BarChart
                layout="horizontal"
                yAxis={[{ scaleType: 'band', data: byRevenue.map((c) => c.customer_name) }]}
                xAxis={[{ valueFormatter: (v: number) => formatMoneyCompact(v, currency) }]}
                series={[
                  {
                    data: byRevenue.map((c) => c.won_value_minor),
                    color: theme.palette.primary.main,
                    valueFormatter: (v) => (v === null ? '—' : formatMoneyCompact(v, currency)),
                  },
                ]}
                margin={{ left: 140 }}
                slotProps={{ legend: { hidden: true } }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={0.25} sx={{ mb: 1 }}>
            <Typography variant="h3">Margin by customer</Typography>
            <Typography variant="caption" color="text.secondary">
              Won business only, {MIN_MARGIN_SAMPLE} quotes minimum
            </Typography>
          </Stack>
          <Box sx={{ height: 320 }}>
            {byMargin.length === 0 ? (
              <Empty>
                No customer has {MIN_MARGIN_SAMPLE} won quotes with a recorded cost yet.
              </Empty>
            ) : (
              <BarChart
                layout="horizontal"
                yAxis={[{ scaleType: 'band', data: byMargin.map((c) => c.customer_name) }]}
                xAxis={[{ valueFormatter: (v: number) => `${v}%` }]}
                series={[
                  {
                    data: byMargin.map((c) => (c.avg_margin_basis_points ?? 0) / 100),
                    color: theme.palette.info.main,
                    valueFormatter: (v, ctx) => {
                      const row = byMargin[ctx.dataIndex];
                      return v === null
                        ? '—'
                        : `${v.toFixed(1)}% over ${row?.margin_sample ?? 0} won quotes`;
                    },
                  },
                ]}
                margin={{ left: 140 }}
                slotProps={{ legend: { hidden: true } }}
              />
            )}
          </Box>

          {excluded > 0 && (
            <Alert severity="info" variant="outlined" sx={{ mt: 1, py: 0 }}>
              {excluded} customer{excluded === 1 ? '' : 's'} not shown — fewer than{' '}
              {MIN_MARGIN_SAMPLE} won quotes with a recorded cost.
            </Alert>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
      <Typography variant="body2" color="text.secondary" align="center">
        {children}
      </Typography>
    </Stack>
  );
}
